/**
 * The Programme Viewer's module table, and what each row opens onto.
 *
 * @module components/reports/ProgrammeModules
 */

import React, { useCallback, useMemo, useState } from "react";

import { recordedCredits } from "@/components/shared/credits";
import {
  Badge,
  CollectionView,
  type Column,
  countLabel,
  EmptyState,
  Eyebrow,
  MutedText,
  Panel,
  Text,
} from "@/components/ui";
import { assessmentCategoryLabel } from "@/reports/assessment-categories";
import type { Module, ModuleAssessment, PLO, Programme } from "@/types";
import {
  classifyAssessmentType,
  formatAssessmentRequirements,
  formatAssessmentTiming,
} from "@/utils/assessments";

const READY = { status: "ready" as const, data: null };

const ASSESSMENT_COLUMNS: readonly Column<ModuleAssessment>[] = [
  { key: "title", header: "Title", render: (a) => a.title ?? "Untitled" },
  {
    key: "type",
    header: "Type",
    render: (a) => (
      <Badge tone="ink" variant="outline" size="xs">
        {assessmentCategoryLabel(classifyAssessmentType(a.type ?? ""))}
      </Badge>
    ),
  },
  {
    key: "weight",
    header: "Weight",
    render: (a) => {
      const weight = a.weighting ?? a.weight;
      return weight == null ? "—" : `${weight}%`;
    },
  },
  { key: "timing", header: "Timing", priority: 2, render: (a) => formatAssessmentTiming(a) },
  {
    key: "requirements",
    header: "Progression requirements",
    priority: 3,
    render: (a) => formatAssessmentRequirements(a),
  },
];

/**
 * The PLOs this module's outcomes serve, and how many of them each reaches.
 * An uncoded PLO is named by its place, as the structure view names it.
 */
function mappedPlos(mod: Module, p: Programme): { plo: PLO; code: string; mimloCount: number }[] {
  const mimloIds = new Set((mod.mimlos ?? []).map((m) => m.id));
  const result: { plo: PLO; code: string; mimloCount: number }[] = [];
  (p.plos ?? []).forEach((plo, i) => {
    const count = ((p.ploToMimlos ?? {})[plo.id] ?? []).filter((id) => mimloIds.has(id)).length;
    if (count > 0) {
      result.push({ plo, code: (plo.code ?? "").trim() || `PLO${i + 1}`, mimloCount: count });
    }
  });
  return result;
}

function ModuleDetailPanel({
  module: mod,
  programme: p,
}: {
  module: Module;
  programme: Programme;
}): React.ReactElement {
  const plos = useMemo(() => mappedPlos(mod, p), [mod, p]);
  const mimlos = mod.mimlos ?? [];
  const assessments = mod.assessments ?? [];
  const reading = mod.readingList ?? [];

  return (
    <div className="space-y-4" data-testid="module-detail-panel">
      {(mod.moduleLeadName || mod.moduleLeadEmail) && (
        <div>
          <Eyebrow>Module lead</Eyebrow>
          <Text className="mt-1">
            {mod.moduleLeadName}
            {mod.moduleLeadEmail && <MutedText as="span"> ({mod.moduleLeadEmail})</MutedText>}
          </Text>
        </div>
      )}

      {mimlos.length > 0 && (
        <div>
          <Eyebrow>Learning outcomes ({mimlos.length})</Eyebrow>
          <ol className="mt-1 list-inside list-decimal space-y-0.5 text-md">
            {mimlos.map((mlo) => (
              <li key={mlo.id}>{mlo.text}</li>
            ))}
          </ol>
        </div>
      )}

      {assessments.length > 0 && (
        <div>
          <Eyebrow>Assessments ({assessments.length})</Eyebrow>
          <CollectionView
            storageKey="report-module-assessments"
            className="mt-1"
            columns={ASSESSMENT_COLUMNS}
            rows={assessments}
            rowKey={(a) => a.id}
            caption={`Assessments for ${mod.title}`}
            density="sm"
            emptyState={<EmptyState title="No assessments." density="row" />}
          />
        </div>
      )}

      {plos.length > 0 && (
        <div>
          <Eyebrow>Mapped PLOs ({plos.length})</Eyebrow>
          <ul className="mt-1 space-y-0.5 text-md">
            {plos.map(({ plo, code, mimloCount }) => (
              <li key={plo.id}>
                <Text as="span" size="sm" className="font-mono">
                  {code}
                </Text>{" "}
                — {plo.text}
                <Badge tone="ink" variant="outline" size="xs" className="ml-1">
                  {countLabel(mimloCount, "module outcome")}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      {reading.length > 0 && (
        <div>
          <Eyebrow>Reading list ({reading.length})</Eyebrow>
          <ul className="mt-1 list-inside list-disc space-y-0.5 text-md">
            {reading.map((r) => (
              <li key={r.id}>
                {r.author && <span>{r.author}. </span>}
                <span className="font-medium">{r.title ?? r.citation}</span>
                {r.year && <span> ({r.year})</span>}
                {r.publisher && <span>. {r.publisher}</span>}
                {r.isCore && (
                  <Badge tone="primary" size="xs" className="ml-1">
                    Core
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const MODULE_COLUMNS: readonly Column<Module>[] = [
  {
    key: "code",
    header: "Code",
    sortValue: (m) => m.code ?? "",
    render: (m) => (
      <Text as="span" className="font-mono">
        {m.code || "—"}
      </Text>
    ),
  },
  {
    key: "title",
    header: "Title",
    sortValue: (m) => m.title,
    render: (m) => <span className="font-medium">{m.title}</span>,
  },
  {
    key: "type",
    header: "Type",
    render: (m) =>
      m.isElective ? (
        <Badge tone="neutral" size="sm">
          Elective
        </Badge>
      ) : (
        <Badge tone="primary" size="sm">
          Mandatory
        </Badge>
      ),
  },
  {
    key: "credits",
    header: "Credits",
    sortValue: (m) => m.credits,
    render: (m) => recordedCredits(m.credits),
  },
  { key: "mimlos", header: "Module outcomes", priority: 3, render: (m) => (m.mimlos ?? []).length },
  {
    key: "assessments",
    header: "Assessments",
    priority: 2,
    render: (m) => (m.assessments ?? []).length,
  },
];

export function ProgrammeModules({
  programme: p,
}: {
  programme: Programme;
}): React.ReactElement | null {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const modules = p.modules ?? [];

  const toggle = useCallback((key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });
  }, []);

  if (modules.length === 0) {
    return null;
  }

  return (
    <Panel
      icon="puzzle-piece"
      title={`Modules (${modules.length})`}
      headingLevel={2}
      flush
      data-testid="modules-section"
    >
      <CollectionView
        storageKey="modules-table"
        columns={MODULE_COLUMNS}
        rows={modules}
        rowKey={(m) => m.id}
        rowTestId={(m) => `module-row-${m.id}`}
        caption={`Modules in ${p.title}`}
        density="sm"
        data-testid="modules-table"
        emptyState={<EmptyState title="No modules." density="row" />}
        expansion={{
          expandedKeys: expanded,
          onToggle: toggle,
          label: (m) => `Details for ${m.title}`,
          toggleTestId: (m) => `module-detail-toggle-${m.id}`,
          state: () => READY,
          render: (m) => <ModuleDetailPanel module={m} programme={p} />,
        }}
      />
    </Panel>
  );
}
